import { Badge, Button } from '@auctisync/ui';
import { useCallback, useEffect, useState } from 'react';
import { invokeEF } from '../../lib/edgeFunctions';
import { supabase } from '../../lib/supabase';
import {
  abuseSignalCopy,
  abuseSignalLabel,
  canSubmit,
  inviteErrorCopy,
  previewSlug,
  TIER_OPTIONS,
} from './superTenantInvite';

/**
 * Super-admin — create an auction house and invite its owner. Onboarding step 0.
 *
 * This is the only way a tenant comes into existence. Before it, every house was
 * inserted by hand against the database, and a new customer had no route to
 * `/admin/onboarding` at all: the wizard resolves which house to configure from
 * `user_roles`, so with no account and no tenant there was nothing to resolve.
 *
 * Invite-first rather than self-serve (Jim, 2026-08-13): houses at $49-$5,000/mo
 * are a sales-led motion. A public signup is what the website demo will need,
 * and can reuse this same path with the human gate removed.
 *
 * Underneath: the abuse trail. "Worth a look" runs deterministic SQL rules over
 * every invite ever sent — same address across houses, same company domain,
 * same internet connection — and the full list follows it.
 *
 * The rules are SQL and not an LLM on purpose: they are a shortlist for a human
 * to read, and paying per row to restate what a GROUP BY already knows would be
 * the wrong end of the cost discipline.
 *
 * The section is framed as "worth a look", never as an accusation, because two
 * of the three signals are routinely innocent. If all three read as alarms, a
 * reader learns to dismiss the whole category — and the IP signal, the one
 * hardest to trigger by accident, is dismissed along with them.
 */

interface InviteRow {
  id: string;
  email: string;
  email_domain: string;
  status: string;
  sent_at: string;
  accepted_at: string | null;
  sent_ip: string | null;
  tenant_id: string;
}

interface TenantName {
  id: string;
  name: string;
}

interface AbuseSignal {
  signal_kind: string;
  signal_value: string;
  tenant_count: number;
  invite_count: number;
  last_seen: string;
  tenant_names: string[];
}

/** Typed bridge for the abuse-signal RPC until `database.ts` regenerates. */
type AbuseRpc = (
  fn: 'tenant_invite_abuse_signals',
) => Promise<{ data: AbuseSignal[] | null; error: { message: string } | null }>;

/**
 * Typed bridge for `tenant_invites` until `database.ts` regenerates.
 *
 * The table IS on production as of 2026-08-13 — this bridge is purely a types
 * lag, not a schema gap. Regeneration was attempted and deliberately deferred:
 * `supabase gen types --linked` 403s on the current CLI login role, and the MCP
 * route emits a 157 KB single-line file whose diff would be unreviewable
 * alongside feature work. It belongs in its own commit.
 *
 * Same shape as the `reset_tenant_onboarding` bridge in SuperOnboardingReset.
 * Delete this once `pnpm db:types` has run.
 */
type InvitesQuery = (table: 'tenant_invites') => {
  select: (cols: string) => {
    order: (
      col: string,
      opts: { ascending: boolean },
    ) => {
      limit: (n: number) => Promise<{ data: unknown[] | null; error: { message: string } | null }>;
    };
  };
};
const invitesFrom = supabase.from.bind(supabase) as unknown as InvitesQuery;
const abuseRpc = supabase.rpc.bind(supabase) as unknown as AbuseRpc;

const cardStyle: React.CSSProperties = {
  border: '1px solid var(--border)',
  borderRadius: '0.6rem',
  padding: '1rem',
  background: 'var(--surface-elev)',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.8rem',
  fontWeight: 600,
  color: 'var(--text-muted)',
  marginBottom: '0.3rem',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.55rem 0.7rem',
  borderRadius: '0.45rem',
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontSize: '0.95rem',
};

export function SuperTenantInvites() {
  const [name, setName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [tier, setTier] = useState<string>(TIER_OPTIONS[0].value);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [invites, setInvites] = useState<InviteRow[] | null>(null);
  const [signals, setSignals] = useState<AbuseSignal[]>([]);
  const [tenantNames, setTenantNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await invitesFrom('tenant_invites')
      .select('id, email, email_domain, status, sent_at, accepted_at, sent_ip, tenant_id')
      .order('sent_at', { ascending: false })
      .limit(200);
    if (!res.error) {
      const rows = (res.data ?? []) as unknown as InviteRow[];
      setInvites(rows);
      // Names in a second read rather than an embedded join — the same two-step
      // the other super pages use, so this doesn't depend on a PostgREST
      // FK-embed shape that a schema change could quietly alter.
      const ids = [...new Set(rows.map((r) => r.tenant_id))];
      if (ids.length > 0) {
        const t = await supabase.from('tenants').select('id, name').in('id', ids);
        const map: Record<string, string> = {};
        for (const row of (t.data ?? []) as unknown as TenantName[]) map[row.id] = row.name;
        setTenantNames(map);
      }
    }
    // Deterministic SQL rules, no LLM — a shortlist for a human to read.
    const sig = await abuseRpc('tenant_invite_abuse_signals');
    if (!sig.error) setSignals(sig.data ?? []);

    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const slug = previewSlug(name);

  async function submit() {
    setBusy(true);
    setError(null);
    setSuccess(null);
    const { data, error: efError } = await invokeEF<{ slug: string; invited_email: string }>(
      'create-tenant-and-invite',
      { name: name.trim(), owner_email: ownerEmail.trim(), tier },
    );
    setBusy(false);
    if (efError !== null) {
      setError(inviteErrorCopy(efError));
      return;
    }
    setSuccess(
      `Created ${name.trim()} and emailed an invitation to ${data?.invited_email ?? ownerEmail.trim()}. Their web address is ${data?.slug ?? slug}.auctisync.com.`,
    );
    setName('');
    setOwnerEmail('');
    void load();
  }

  return (
    <main style={{ padding: '2rem', maxWidth: '56rem', margin: '0 auto' }}>
      <h1
        style={{
          fontFamily: 'var(--font-heading-active)',
          fontSize: '1.75rem',
          fontWeight: 700,
          color: 'var(--text)',
          margin: 0,
        }}
      >
        New auction house
      </h1>
      <p style={{ color: 'var(--text-muted)', margin: '0.4rem 0 1.5rem', maxWidth: '42rem' }}>
        Creates the house and emails its owner an invitation. They choose their own password and
        land on the setup checklist. Nothing is public until they finish setting up and choose to go
        live.
      </p>

      <section style={{ ...cardStyle, marginBottom: '2rem' }}>
        <div style={{ display: 'grid', gap: '1rem' }}>
          <div>
            <label style={labelStyle} htmlFor="house-name">
              Auction house name
            </label>
            <input
              id="house-name"
              style={inputStyle}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Dan's Discount Tools"
            />
            {/* Shown live because the slug becomes their subdomain and is
                awkward to change afterwards. */}
            {name.trim() !== '' && (
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: '0.35rem 0 0' }}>
                {slug === '' ? (
                  <span style={{ color: 'var(--semantic-danger)' }}>
                    That name has no letters or numbers, so it cannot become a web address.
                  </span>
                ) : (
                  <>
                    Web address: <strong style={{ color: 'var(--text)' }}>{slug}</strong>
                    .auctisync.com
                  </>
                )}
              </p>
            )}
          </div>

          <div>
            <label style={labelStyle} htmlFor="owner-email">
              Owner&apos;s email
            </label>
            <input
              id="owner-email"
              style={inputStyle}
              type="email"
              value={ownerEmail}
              onChange={(e) => setOwnerEmail(e.target.value)}
              placeholder="dan@example.com"
            />
            <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: '0.35rem 0 0' }}>
              The invitation goes here. This becomes their sign-in.
            </p>
          </div>

          <div>
            <label style={labelStyle} htmlFor="tier">
              Plan
            </label>
            <select
              id="tier"
              style={inputStyle}
              value={tier}
              onChange={(e) => setTier(e.target.value)}
            >
              {TIER_OPTIONS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <Button onClick={() => void submit()} disabled={!canSubmit({ name, ownerEmail, busy })}>
              {busy ? 'Creating…' : 'Create house and send invitation'}
            </Button>
          </div>

          {error !== null && (
            <div
              role="alert"
              style={{
                border: '1px solid var(--semantic-danger)',
                borderRadius: '0.45rem',
                padding: '0.7rem 0.85rem',
                color: 'var(--semantic-danger)',
                fontSize: '0.9rem',
              }}
            >
              {error}
            </div>
          )}
          {success !== null && (
            <p style={{ color: 'var(--semantic-success)', margin: 0, fontSize: '0.9rem' }}>
              ✓ {success}
            </p>
          )}
        </div>
      </section>

      {signals.length > 0 && (
        <section style={{ marginBottom: '2rem' }}>
          <h2
            style={{
              fontFamily: 'var(--font-heading-active)',
              fontSize: '1.15rem',
              fontWeight: 700,
              color: 'var(--text)',
              margin: '0 0 0.25rem',
            }}
          >
            Worth a look
          </h2>
          <p
            style={{
              color: 'var(--text-muted)',
              fontSize: '0.85rem',
              margin: '0 0 0.75rem',
              maxWidth: '42rem',
            }}
          >
            Patterns across invitations. Most have an innocent explanation — these are things to
            check, not accusations.
          </p>
          <div style={{ display: 'grid', gap: '0.6rem' }}>
            {signals.map((s) => (
              <div key={`${s.signal_kind}:${s.signal_value}`} style={cardStyle}>
                <div
                  style={{
                    display: 'flex',
                    gap: '0.6rem',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    marginBottom: '0.35rem',
                  }}
                >
                  <Badge variant={s.signal_kind === 'ip' ? 'warning' : 'secondary'}>
                    {abuseSignalLabel(s.signal_kind)}
                  </Badge>
                  <strong style={{ color: 'var(--text)' }}>{s.signal_value}</strong>
                </div>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.88rem', margin: 0 }}>
                  {abuseSignalCopy(s.signal_kind, s.tenant_count)}
                </p>
                <p
                  style={{ color: 'var(--text-muted)', fontSize: '0.82rem', margin: '0.35rem 0 0' }}
                >
                  Houses: {s.tenant_names.join(', ')}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      <h2
        style={{
          fontFamily: 'var(--font-heading-active)',
          fontSize: '1.15rem',
          fontWeight: 700,
          color: 'var(--text)',
          margin: '0 0 0.75rem',
        }}
      >
        Invitations sent
      </h2>

      {loading ? (
        <p style={{ color: 'var(--text-muted)' }}>Loading…</p>
      ) : (invites ?? []).length === 0 ? (
        <p style={{ color: 'var(--text-muted)' }}>No invitations yet.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--text-muted)' }}>
                <th style={{ padding: '0.5rem 0.6rem' }}>House</th>
                <th style={{ padding: '0.5rem 0.6rem' }}>Email</th>
                <th style={{ padding: '0.5rem 0.6rem' }}>Company domain</th>
                <th style={{ padding: '0.5rem 0.6rem' }}>Status</th>
                <th style={{ padding: '0.5rem 0.6rem' }}>Sent</th>
                <th style={{ padding: '0.5rem 0.6rem' }}>From IP</th>
              </tr>
            </thead>
            <tbody>
              {(invites ?? []).map((r) => (
                <tr key={r.id} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '0.5rem 0.6rem', color: 'var(--text)' }}>
                    {tenantNames[r.tenant_id] ?? '—'}
                  </td>
                  <td style={{ padding: '0.5rem 0.6rem', color: 'var(--text)' }}>{r.email}</td>
                  <td style={{ padding: '0.5rem 0.6rem', color: 'var(--text-muted)' }}>
                    {r.email_domain}
                  </td>
                  <td style={{ padding: '0.5rem 0.6rem' }}>
                    <Badge variant={r.status === 'accepted' ? 'success' : 'secondary'}>
                      {r.status}
                    </Badge>
                  </td>
                  <td style={{ padding: '0.5rem 0.6rem', color: 'var(--text-muted)' }}>
                    {new Date(r.sent_at).toLocaleDateString()}
                  </td>
                  <td style={{ padding: '0.5rem 0.6rem', color: 'var(--text-muted)' }}>
                    {r.sent_ip ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}
